import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { config } from '../../config/config';

const ProjectManagerSearch = ({ selectedManagers, onManagerSelect, onManagerRemove }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const token = useSelector(state => state.auth.token);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchManagers = async (term) => {
    if (!term) {
      setManagers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/api/users/project-managers?search=${term}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      // Filter out already selected managers
      const filteredManagers = data.users.filter(
        manager => !selectedManagers.some(selected => selected._id === manager._id)
      );
      
      setManagers(filteredManagers);
    } catch (error) {
      console.error('Error searching managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
    searchManagers(term);
  };

  const handleSelect = (manager) => {
    onManagerSelect(manager);
    setSearchTerm('');
    setManagers([]);
    setShowDropdown(false);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <div className="relative">
      <div>
        {/* Selected managers pills */}
        {selectedManagers.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedManagers.map(manager => (
              <div 
                key={manager._id}
                className="flex items-center bg-blue-50 rounded-full pl-2 pr-3 py-1"
              >
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs mr-2">
                  {getInitials(manager.profile.firstName, manager.profile.lastName)}
                </div>
                <span className="text-sm text-blue-800">
                  {manager.profile.firstName} {manager.profile.lastName}
                </span>
                <button
                  type="button"
                  onClick={() => onManagerRemove(manager._id)}
                  className="ml-2 text-blue-500 hover:text-blue-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Search input */}
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search team members..."
          className="w-full border border-gray-200 px-4 py-2.5 focus:outline-none focus:border-blue-500 text-gray-900"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (searchTerm || loading) && (
        <div 
          ref={dropdownRef}
          className="absolute z-10 w-full bg-white shadow-lg border border-gray-200 rounded-md mt-1 max-h-60 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : managers.length > 0 ? (
            <ul>
              {managers.map(manager => (
                <li 
                  key={manager._id}
                  className="hover:bg-gray-50 cursor-pointer p-3 flex items-center"
                  onClick={() => handleSelect(manager)}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm mr-3">
                    {getInitials(manager.profile.firstName, manager.profile.lastName)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {manager.profile.firstName} {manager.profile.lastName}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <span className="mr-2">{manager.profile.company?.position || 'N/A'}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        manager.profile.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                        manager.profile.role === 'admin' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {manager.profile.role === 'super_admin' ? 'Super Admin' :
                         manager.profile.role === 'admin' ? 'Admin' : 
                         'Project Manager'}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center text-gray-500">
              No assignable users found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectManagerSearch; 